import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { courses } from "../../data/constants";
import styled from "styled-components";
import { HashLink } from "react-router-hash-link";
import EnrollmentForm from "./EnrollmentForm";

const Wrapper = styled.div`
  max-width: 800px;
  margin: 50px auto;
  padding: 20px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text_primary};
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
`;

const Title = styled.div`
  font-size: 25px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin: 8px 6px 0px 6px;
  @media only screen and (max-width: 600px) {
    font-size: 24px;
    margin: 6px 6px 0px 6px;
  }
`;

const Desc = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_primary};
  margin: 8px 6px;
  @media only screen and (max-width: 600px) {
    font-size: 14px;
    margin: 6px 6px;
  }
`;

const SectionHeader = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.text_primary};
`;

const Image = styled.img`
  width: 100%;
  object-fit: cover;
  border-radius: 12px;
  margin-top: 30px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.3);
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 8px 0px;
  @media only screen and (max-width: 600px) {
    margin: 4px 0px;
  }
`;

const Tag = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.primary};
  margin: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.primary + 20};
  @media only screen and (max-width: 600px) {
    font-size: 12px;
  }
`;

const SideBySideSections = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  gap: 20px;

  @media only screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const Section = styled.div`
  width: 48%;
  background-color: ${({ theme }) => theme.bgLight};
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);

  @media only screen and (max-width: 768px) {
    width: 100%;
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 12px 0px;
  gap: 12px;
`;

const Button = styled.a`
  width: 100%;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.primary};
  ${({ dull, theme }) =>
    dull &&
    `
    background-color: ${theme.bgLight};
    color: ${theme.text_secondary};
    &:hover {
      background-color: ${theme.bg + 99};
    }
  `}
  cursor: pointer;
  text-decoration: none;
  transition: all 0.5s ease;
  &:hover {
    background-color: ${({ theme }) => theme.primary + 99};
  }
  @media only screen and (max-width: 600px) {
    font-size: 12px;
  }
`;

const CoursePage = () => {
  const { slug } = useParams();
  const course = courses.find((c) => c.slug === slug);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);

  if (!course) return <h2>Course not found</h2>;

  return (
    <>
      <Wrapper>
        <Image src={course.image} alt={course.title} />
        <Title>{course.title}</Title>
        <Tags>
          {course.tags?.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </Tags>

        <Desc>
          <SectionHeader>About this Course</SectionHeader>
          {Array.isArray(course.description) ? (
            course.description.map((desc, i) => (
              <p key={i} style={{ marginBottom: "1rem" }}>
                {desc}
              </p>
            ))
          ) : (
            <p>{course.description}</p>
          )}
        </Desc>

        <Desc>
          <SectionHeader>Methodology</SectionHeader>
          <p>{course.methodology || "Theoritical/Practical"}</p>
        </Desc>

        <Desc>
              <SectionHeader>Prerequisite</SectionHeader>
              {course?.prerequisite?.length > 0 ? (
                <div>
                  {course.prerequisite.map((item, idx) => (
                    item.includes("\n") ? (
                      item.split("\n").map((line, index) => {
                        if (line.trim().endsWith(":")) {
                          return (
                            <p
                              key={`${idx}-${index}`}
                              style={{
                                fontWeight: "bold",
                                marginTop: "1rem",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {line.replace("·", "").trim()}
                            </p>
                          );
                        }
                        return (
                          <li
                            key={`${idx}-${index}`}
                            style={{ marginLeft: "1.5rem", marginBottom: "0.5rem" }}
                          >
                            {line.replace("·", "").trim()}
                          </li>
                        );
                      })
                    ) : (
                      <li
                        key={idx}
                        style={{ marginLeft: "1.5rem", marginBottom: "0.5rem" }}
                      >
                        {item.trim()}
                      </li>
                    )
                  ))}
                </div>
              ) : (
                <p>No prerequisites available</p>
              )}
            </Desc>

        <SideBySideSections>
          <Section>
            <SectionHeader>Learning outcomes</SectionHeader>
            <p style={{ marginBottom: "0.5rem", fontWeight: "500" }}>
              By the end of this course, participants will be able to:
            </p>
            <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
              {course.objectives?.map((obj, i) => (
                <li key={i} style={{ marginBottom: "0.5rem" }}>
                  {obj}
                </li>
              ))}
            </ul>
          </Section>
          <Section>
            <SectionHeader>Course outline</SectionHeader>
            <ul style={{ listStyleType: "none", paddingLeft: "0" }}>
              {course.outline?.map((item, i) => (
                <li key={i} style={{ marginBottom: "0.3rem" }}>
                  {item}
                </li>
              ))}
            </ul>
          </Section>
        </SideBySideSections>

        {course.additionalSections?.map((section) => (
          <Desc key={section.title}>
            <SectionHeader>{section.title}</SectionHeader>
            <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
              {section.items?.map((item, i) => (
                <li key={`${item}-${i}`} style={{ marginBottom: "0.5rem" }}>
                  {item}
                </li>
              ))}
            </ul>
          </Desc>
        ))}

        <Desc>
          <SectionHeader>Assessment</SectionHeader>
          <p>{course.assessment}</p>
        </Desc>

        <Desc>
          <SectionHeader>Course Delivery Formats</SectionHeader>
          <p>{course.cdf?.join("/") || "Online/Virtual"}</p>
        </Desc>

        <Desc>
          <SectionHeader>Cost</SectionHeader>
          <p>{course.cost}</p>
        </Desc>

        <ButtonGroup>
          <Button onClick={() => setEnrollmentOpen(true)}>Enroll Now</Button>
          <Button as={HashLink} smooth to="/#contact" dull>
            Make Enquiry
          </Button>
        </ButtonGroup>
      </Wrapper>

      <EnrollmentForm
        isOpen={enrollmentOpen}
        onClose={() => setEnrollmentOpen(false)}
        course={course}
      />
    </>
  );
};

export default CoursePage;
